/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * publishModel — the declare-then-publish rules, with no network and no React.
 *
 * Three kinds of position live in this feature and they are NOT the same thing:
 *
 *   position_overall   on event_result — the finishing order, worked out from
 *                      the times. Nobody decides it. Everyone who finished.
 *   declared_position  on event_result — the podium marked by hand. A DRAFT,
 *                      not public. Only the winners.
 *   position           on the award table — the PUBLISHED placing. The official
 *                      record. Only the winners, once published.
 *
 * The draft may deliberately disagree with the finishing order — a
 * disqualification, a wave start, a category awarded separately — which is
 * exactly why it is a separate column and not derived.
 */

/** Statuses that count as having finished the race. */
const FINISHED = 'FINISHED';

/**
 * Empty is not allowed on a published award's category.
 *
 * The table enforces one placing per category per race, and a database treats
 * two empty values as different — so two empty-category first places would both
 * be accepted. Writing the word makes the rule bite.
 */
export const OVERALL = 'Overall';

/**
 * Finishing order, worked out from the times.
 *
 * Standard competition ranking: equal times share a place and the next place
 * skips, so two runners tied for 2nd are followed by 4th, never by 3rd.
 * Results with no time are left out — they did not finish, so they have no
 * finishing position at all.
 */
export function rankedPositions(results) {
  const finishers = (results || [])
    .filter((r) => r && r.status === FINISHED && Number(r.duration_seconds) > 0)
    .sort((a, b) => Number(a.duration_seconds) - Number(b.duration_seconds));

  const ranked = [];
  let lastTime = null;
  let lastPosition = 0;
  finishers.forEach((row, index) => {
    const time = Number(row.duration_seconds);
    const position = time === lastTime ? lastPosition : index + 1;
    ranked.push({ id: row.id, position_overall: position });
    lastTime = time;
    lastPosition = position;
  });
  return ranked;
}

/** Only the rows whose draft podium place is set, lowest place first. */
export function declaredWinners(results) {
  return (results || [])
    .filter((r) => r && Number(r.declared_position) >= 1)
    .sort((a, b) => Number(a.declared_position) - Number(b.declared_position));
}

/**
 * The award rows to write for a race.
 *
 * The three names are frozen copies taken now, on purpose: an award is an
 * announcement, so renaming a race or a profile later must not rewrite what was
 * read out on the day.
 */
export function awardRowsFor({ results, eventActivityId, activityName, eventName }) {
  return declaredWinners(results).map((row) => ({
    event_activity_id: eventActivityId,
    event_result_id: row.id,
    // THE ATHLETE, not whoever wrote the result row. created_by_id is the
    // organizer on every result they attached, so an award built from it
    // credited the whole podium to the organizer. athlete_user_id comes off
    // the registration; created_by_id stays as the fallback for a row read
    // before that column existed.
    user_id: Number(row.athlete_user_id ?? row.created_by_id),
    user_name: String(row.athlete_name || 'Athlete').slice(0, 128),
    activity_name: String(activityName || '').slice(0, 128),
    event_name: String(eventName || '').slice(0, 200),
    position: Number(row.declared_position),
    // Never empty — see OVERALL above.
    category: OVERALL,
  }));
}

/**
 * Why this set of winners cannot be published yet, or null when it can.
 *
 * Checked here rather than left to the database so the organizer reads a
 * sentence instead of a constraint violation.
 */
export function publishBlocker(results) {
  const winners = declaredWinners(results);
  if (winners.length === 0) {
    return 'Mark at least one winner before publishing.';
  }
  const places = winners.map((w) => Number(w.declared_position));
  const duplicate = places.find((place, i) => places.indexOf(place) !== i);
  if (duplicate !== undefined) {
    return `Two athletes are both marked ${duplicate}. Each place can only go to one person.`;
  }
  const nameless = winners.find((w) => !w.athlete_name);
  if (nameless) {
    return 'One of the winners has no name on their result, so there is nothing to announce.';
  }
  // Same column the award will carry — checking the other one would pass a
  // winner the write then refuses.
  const ownerless = winners.find((w) => !Number(w.athlete_user_id ?? w.created_by_id));
  if (ownerless) {
    return 'One of the winners has no athlete on their result. Reload and try again.';
  }
  return null;
}
